import React from 'react'

// models
import { OpenChallengeType } from 'models/challenge'

// third party
import { NextRouter } from 'next/router'
import Tippy from '@tippyjs/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons'
import SimpleReactValidator from 'simple-react-validator'

//services
import QuestionService from 'services/question.service'

//utils & hooks
import { answersValid } from 'utils/questions'
import { countWords } from 'utils/helpers'
import withRouterForwardRef from 'hooks/withRouterForwardRef'

// componentents
import FormCompanyQuestions from '../../account/forms/FormCompanyQuestions'
import GenerateChallengeSection from 'components/shared/generate-challenge/GenerateChallengeSection'

//Fields
import Checkbox from 'components/account/shared/Fields/Checkbox'
import RequiredSymbol from '../../shared/RequiredSymbol'

interface WithRouterProps {
	router: NextRouter
}

interface ChallengeSubmitFormProps extends WithRouterProps {
	onDataChange: Function
	data: any
	user?: any
	organisationId?: string
	isOpenChallenge?: boolean
	forceOpenChallengeType?: string
	showOpenChallenge?: boolean
	showGenerateAiButton?: boolean
}

interface ChallengeSubmitFormState {
	title: string
	id: string
	summary: string
	description: string
	teamCountPreference: string | number
	companyData: {
		company: {}
		address: {}
	}
	companyUsers: any
	openChallengeType: string
	isOpenChallenge?: boolean
	showOpenChallenge?: boolean
	isInternalChallenge?: boolean
	challengeSpaceId?: string
	questions: any
	answers: {}
}

class ChallengeSubmitForm extends React.Component<
	ChallengeSubmitFormProps,
	ChallengeSubmitFormState
> {
	validator = new SimpleReactValidator({
		autoForceUpdate: this,
		locale: 'en',
		messages: {
			required: 'This field is required',
		},
		validators: {
			maxwords: {
				// name the rule
				message: 'A maximum of :values words is allowed',
				rule: (val, params) => {
					return countWords(val) <= params
				},
				// @ts-ignore
				messageReplace: (message, params) => message.replace(':values', params), // optional
			},
		},
		element: (message) => (
			<p className="text-red-500 text-xs mt-1">{message}</p>
		),
	})

	constructor(props: ChallengeSubmitFormProps) {
		super(props)

		this.state = {
			title: '',
			summary: '',
			id: '',
			description: '',
			teamCountPreference: '',
			companyData: {
				address: {},
				company: {},
			},
			companyUsers: [],
			openChallengeType:
				props.forceOpenChallengeType ??
				(props.isOpenChallenge
					? OpenChallengeType[OpenChallengeType.global]
					: OpenChallengeType[OpenChallengeType.private]),
			showOpenChallenge: false,
			isOpenChallenge: props.isOpenChallenge ?? false,
			isInternalChallenge: false,
			questions: [],
			answers: {},
		}

		this.handleInputChange = this.handleInputChange.bind(this)
		this.changeParentData = this.changeParentData.bind(this)
		this.handleOpenChallengeChange = this.handleOpenChallengeChange.bind(this)
		this.getOpenChallengeType = this.getOpenChallengeType.bind(this)
	}

	handleChange(name, value) {
		this.setState(
			{
				[name]: value,
			} as unknown as Pick<
				ChallengeSubmitFormState,
				keyof ChallengeSubmitFormState
			>,
			() => this.changeParentData()
		)
	}

	handleInputChange(event: any) {
		const target = event.target
		const value = target.type == 'checkbox' ? target.checked : target.value
		const name = target.name

		this.handleChange(name, value)
	}

	handleOpenChallengeChange(event: any) {
		const target = event.target
		this.handleChange(
			'openChallengeType',
			this.getOpenChallengeType(target.checked)
		)
	}

	getOpenChallengeType(allowGlobalOpenChallenge) {
		return allowGlobalOpenChallenge
			? OpenChallengeType[OpenChallengeType.global]
			: OpenChallengeType[OpenChallengeType.organisation]
	}

	requiredQuestions = () =>
		this.state.questions.filter((a) => a.options.required || a.type === 'text')

	questionAnswersValid = () => {
		return answersValid({
			requiredQuestions: this.requiredQuestions(),
			answers: this.state.answers,
		})
	}

	componentDidMount() {
		const { challengeSpaceId } = this.props.router.query

		this.setState({
			...this.state,
			...this.props.data,
			challengeSpaceId: challengeSpaceId,
		})

		this.getChallengeSpaceQuestions(challengeSpaceId)
	}

	getChallengeSpaceQuestions(challengeSpaceId) {
		QuestionService.getCompanyQuestionsFormatted({
			parentId: challengeSpaceId,
			context: 'challenges',
		}).then((res) => {
			this.setState({
				questions: res,
			})
		})
	}

	changeParentData() {
		this.props.onDataChange(
			'challengeFormData',
			this.state,
			this.validator.allValid() && this.questionAnswersValid()
		)
	}

	render() {
		this.validator.purgeFields()

		return (
			<>
				<form onSubmit={this.changeParentData} className="pt-6 mb-4" noValidate>
					{this.props.showGenerateAiButton && (
						<div className="mb-4">
							<GenerateChallengeSection />
						</div>
					)}

					<div className="mb-4">
						<label htmlFor="title">
							<RequiredSymbol />
							Challenge title
						</label>
						<p className="text-sm mb-2">
							Short one sentence description of the challenge
						</p>
						<input
							className="w-full"
							name="title"
							maxLength={150}
							value={this.state.title}
							onChange={this.handleInputChange}
							onBlur={() => this.validator.showMessageFor('title')}
							id="title"
							type="text"
							placeholder="Challenge title"
						/>
						{this.validator.message(
							'title',
							this.state.title,
							'required|max:150|min:3'
						)}
					</div>

					<div className="mb-6">
						<label htmlFor="description">
							<RequiredSymbol />
							Challenge description
						</label>
						<p className="text-sm mb-2">
							Provide some context about the challenge
						</p>
						<textarea
							className="w-full"
							name="description"
							rows={6}
							value={this.state.description}
							onChange={this.handleInputChange}
							onBlur={() => this.validator.showMessageFor('description')}
							id="description"
							placeholder="Description"
						></textarea>
						{this.validator.message(
							'description',
							this.state.description,
							'required|maxwords:5000|min:3'
						)}
					</div>
					<div className="mb-4">
						<label htmlFor="teamSizeLimit">
							<RequiredSymbol />
							Preferred amount of teams
						</label>
						<input
							className="w-full"
							name="teamCountPreference"
							min="1"
							value={this.state.teamCountPreference}
							id="teamCountPreference"
							onChange={this.handleInputChange}
							onBlur={() =>
								this.validator.showMessageFor('preferred team count')
							}
							type="number"
							placeholder="Enter a number"
						/>
						{this.validator.message(
							'preferred team count',
							this.state.teamCountPreference,
							'min:1,num|integer|required'
						)}
						<small className="text-gray-700">
							How many teams would you preferably like to assign to this
							challenge?
							<br />
							<strong>Note</strong>: availability depends on the amount of teams
							that take part in this period.
						</small>
					</div>

					{(this.props.showOpenChallenge || this.state.showOpenChallenge) && (
						<div className="mb-4">
							<Checkbox
								checked={this.state.openChallengeType === 'global'}
								className=""
								label={
									<div className="block pb-2 ml-3">
										<label
											htmlFor="isOpenChallenge"
											className="font-semibold inline"
											// className="inline"
										>
											Make my challenge also visible to lecturers from other
											educational institutions.
										</label>
										<Tippy
											theme="light"
											arrow={true}
											placement="top"
											content={
												<div className="text-center p-2">
													<p>
														Selecting this will give your open challenge a
														larger reach. More lecturers from different
														educational institutions can see this open
														challenge, and might reach out to you to discuss how
														your challenge might fit their courses.
													</p>
												</div>
											}
											allowHTML={true}
											className="cursor-pointer block"
										>
											<a href="#">
												<FontAwesomeIcon
													className="ml-1 text-yellow-600"
													icon={faInfoCircle}
												/>
											</a>
										</Tippy>
									</div>
								}
								name="isOpenChallenge"
								id="isOpenChallenge"
								onChange={this.handleOpenChallengeChange}
							/>
						</div>
					)}

					<FormCompanyQuestions
						validator={this.validator}
						answers={this.state.answers}
						parentQuestions={this.state.questions}
						setAnswers={({ data, questionId }) => {
							this.setState(
								() => {
									//Formatting the answer to match BE reset expectations - set to null to reset
									const a = data.answer
									const formattedAnswer =
										!a || (Array.isArray(a) && !a.length) ? null : a

									return {
										...this.state,
										answers: {
											...this.state.answers,
											[questionId]: {
												...this.state.answers[questionId],
												answer: formattedAnswer, //select value ID of raw string
												context_type: 'challenge',
												questionId: questionId, // qeustion id
											},
										},
									}
								},
								() => this.changeParentData()
							)
						}}
					/>
				</form>
			</>
		)
	}
}

export default withRouterForwardRef(ChallengeSubmitForm)
